import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import TableHead from './SubFormHead';
import TableBody from './SubFormBody';
import SubFormRows from './SubFormRows';
import { getUserInfo } from '../../../../Utils/UserFunctions';
import { isSet } from '../../../../Utils/CommonFunctions';
import 'react-virtualized/styles.css';

const SUBFORM_VIRTUAL_ROW_THRESHOLD = 15;
const SUBFORM_VIRTUAL_ROW_HEIGHT = 60;
const SUBFORM_TABLE_SCROLL_MAX_HEIGHT = 420;

function getSectionData(section) {
  let sectionData = section?.data || [];
  const userInfo = getUserInfo();
  if (userInfo?.data?.role_name === 'Community Partners') {
    sectionData = sectionData.filter(
      (item) => item.api_custom_field_name !== 'cf_quoted_cost'
    );
  }
  return sectionData;
}

const SubFormTable = (props) => {
  const { headProps, bodyProps } = props;
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [showScrollLoading, setShowScrollLoading] = useState(false);
  const isDropdownOpenRef = useRef(false);
  const scrollLoadingTimeoutRef = useRef(null);

  const pageType = bodyProps.pageType;
  const rows = bodyProps.sectionSubformData?.subform_rows || [];
  const rowCount = rows.length;
  const layoutObject = bodyProps.layoutObject;
  const windowName = layoutObject?.windowName;

  const shouldVirtualize =
    pageType === 'form' &&
    rowCount > SUBFORM_VIRTUAL_ROW_THRESHOLD &&
    windowName !== 'Shipments';

  const section = bodyProps.section || {};
  const sectionData = getSectionData(section);
  
  const showLoadingOverlay = shouldVirtualize && bodyProps.showLoadingOverlay === true;
  const showOverlay = showLoadingOverlay || showScrollLoading;

  const handleScroll = useCallback((e) => {
    const el = e.currentTarget;
    const newScrollTop = e.target.scrollTop;
    const OPEN_DD = '.dropdown-menu.show, .dropdown-menu[style*="display: block"]';
    const dropdown = el.querySelector(OPEN_DD);

    if (dropdown) {
      if (dropdown.matches(':hover')) {
        return;
      }
      // Close dropdown if scrolling outside
      dropdown.classList.remove('show');
      dropdown.style.display = '';
      const parent = dropdown.closest('.customized-control');
      if (parent) parent.classList.remove('show');
    }

    setScrollTop((prevScrollTop) => {
      // Only show loading and trigger a re-render if we scrolled vertically
      if (prevScrollTop !== newScrollTop) {
        setShowScrollLoading(true);
        return newScrollTop;
      }
      return prevScrollTop;
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const OPEN_DD = '.dropdown-menu.show, .dropdown-menu[style*="display: block"]';

    const observer = new MutationObserver(() => {
      const open = !!el.querySelector(OPEN_DD);
      if (isDropdownOpenRef.current !== open) {
        isDropdownOpenRef.current = open;
      }
    });

    const onWheel = (e) => {
      if (!isDropdownOpenRef.current) return;
      const dropdown = el.querySelector(OPEN_DD);
      if (!dropdown || dropdown.contains(e.target)) return;
      dropdown.classList.remove('show');
      dropdown.style.display = '';
      const parent = dropdown.closest('.customized-control');
      if (parent) parent.classList.remove('show');
      el.style.overflow = 'auto';
      isDropdownOpenRef.current = false;
    };

    observer.observe(el, { subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    document.addEventListener('wheel', onWheel, true);
    return () => {
      observer.disconnect();
      document.removeEventListener('wheel', onWheel, true);
    };
  }, []);

  useEffect(() => {
    const index = bodyProps.scrollToRowIndex;
    const onScrolledToRow = bodyProps.onScrolledToRow;
    if (index == null || typeof onScrolledToRow !== 'function') return;
    if (shouldVirtualize && scrollRef.current) {
      try{
        scrollRef.current.scrollTop = index * SUBFORM_VIRTUAL_ROW_HEIGHT;
      }catch(error){
        console.error("Error scrolling to row", error);
      }
    }
    onScrolledToRow();
  }, [bodyProps.scrollToRowIndex, bodyProps.onScrolledToRow, shouldVirtualize, scrollRef]);

  useEffect(() => {
    if (!shouldVirtualize) return;
    if (scrollLoadingTimeoutRef.current) clearTimeout(scrollLoadingTimeoutRef.current);
    scrollLoadingTimeoutRef.current = setTimeout(() => {
      scrollLoadingTimeoutRef.current = null;
      setShowScrollLoading(false);
    }, 120);
    return () => {
      if (scrollLoadingTimeoutRef.current) clearTimeout(scrollLoadingTimeoutRef.current);
    };
  }, [scrollTop, shouldVirtualize]);

  if (shouldVirtualize) {
    const overscan = 5;
    const startIndex = Math.max(0, Math.floor(scrollTop / SUBFORM_VIRTUAL_ROW_HEIGHT) - overscan);
    const endIndex = Math.min(rowCount - 1, Math.ceil((scrollTop + SUBFORM_TABLE_SCROLL_MAX_HEIGHT) / SUBFORM_VIRTUAL_ROW_HEIGHT) + overscan);

    const topSpacerHeight = startIndex * SUBFORM_VIRTUAL_ROW_HEIGHT;
    const bottomSpacerHeight = Math.max(0, (rowCount - 1 - endIndex) * SUBFORM_VIRTUAL_ROW_HEIGHT);

    const visibleSectionData = sectionData.filter(
        (item) =>
        isSet(item.access_mode, 'EDITABLE') !== 'HIDDEN' &&
        isSet(item.is_add_form, false) === true
    );
    const visibleColCount = (bodyProps.isWrite ? 1 : 0) + visibleSectionData.length;

    return (
      <div style={showOverlay ? { position: 'relative' } : undefined}>
        <div 
          ref={scrollRef} 
          onScroll={handleScroll}
          style={{ 
            height: SUBFORM_TABLE_SCROLL_MAX_HEIGHT, 
            overflow: 'auto',
            position: 'relative',
            overscrollBehavior: 'contain'
          }}
        >
          <table className="subformTable table nowrap mb-0 table-striped">
            <TableHead {...headProps} isVirtualized={true} />
            <tbody>
              {topSpacerHeight > 0 && (
                <tr style={{ height: topSpacerHeight }}>
                  <td colSpan={visibleColCount} style={{ padding: 0, border: 0 }} />
                </tr>
              )}
              {rows.slice(startIndex, endIndex + 1).map((rowData, localIndex) => {
                const index = startIndex + localIndex;
                return (
                  <SubFormRows
                    key={rowData.id || index}
                    rowIndex={index}
                    dataRowIndex={index}
                    sectionData={sectionData}
                    rowData={rowData}
                    modType={bodyProps.modType}
                    isWrite={bodyProps.isWrite}
                    updateSubformRow={bodyProps.updateSubformRow}
                    saveAndUpdateSubform={bodyProps.saveAndUpdateSubform}
                    sectionSubformData={bodyProps.sectionSubformData}
                    pageType={bodyProps.pageType}
                    reminderDisplayText={bodyProps.reminderDisplayText}
                    section={section}
                    removeSubformDataHandler={bodyProps.removeSubformDataHandler}
                    onChangeInputAction={bodyProps.onChangeInputAction}
                    subformIndex={bodyProps.subformIndex}
                    shouldRenderButton={bodyProps.shouldRenderButton}
                    isSelectAll={bodyProps.isSelectAll}
                    selectData={bodyProps.selectHandler}
                    onSubformRowAdded={bodyProps.onSubformRowAdded}
                    sectionType={bodyProps.sectionType}
                    virtualizedRowHeight={SUBFORM_VIRTUAL_ROW_HEIGHT}
                    onSubformActionStart={bodyProps.onSubformActionStart}
                    windowName={bodyProps.windowName}
                  />
                );
              })}
              {bottomSpacerHeight > 0 && (
                <tr style={{ height: bottomSpacerHeight }}>
                  <td colSpan={visibleColCount} style={{ padding: 0, border: 0 }} />
                </tr>
              )}
              {rowCount === 0 && (
                <tr>
                  <td colSpan={visibleColCount} style={{ padding: 16, textAlign: 'center', color: '#666' }}>
                    No Record exists
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {showOverlay && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.9)',
              zIndex: 14,
              pointerEvents: 'auto',
            }}
            aria-live="polite"
            aria-busy="true"
          >
            <span style={{ fontSize: '14px', color: '#333', fontWeight: 500 }}>
              Loading...
            </span>
          </div>
        )}
      </div>
    );
  }

  const tableContent = (
    <table className="subformTable table nowrap mb-0 table-striped">
      <TableHead {...headProps} isVirtualized={false} />
      <TableBody {...bodyProps} />
    </table>
  );

  return <div>{tableContent}</div>;
};

export default memo(SubFormTable);
